import SearchIcon from "@mui/icons-material/Search";
import { Autocomplete, Box, Checkbox, Chip, Popper } from "@mui/material";
import { styled } from "@mui/material/styles";

interface ChipContainerProps {
  hasSelections?: boolean;
  isOpen?: boolean;
}

export const ChipContainer = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "hasSelections" && prop !== "isOpen",
})<ChipContainerProps>(({ theme, hasSelections, isOpen }) => ({
  borderRadius: "100px",
  paddingTop: "3px",
  paddingBottom: "3px",
  paddingLeft: "4px",
  paddingRight: "4px",
  height: "24px",
  minHeight: "24px",
  maxHeight: "24px",
  fontSize: theme.typography.body2.fontSize,
  fontWeight: hasSelections
    ? theme.typography.fontWeightMedium
    : theme.typography.fontWeightRegular,
  cursor: "pointer",
  transition: theme.transitions.create(["background-color", "border-color", "box-shadow"], {
    duration: theme.transitions.duration.short,
  }),

  // Initial state (no selections): dashed border, white background
  ...(!hasSelections && {
    borderStyle: "dashed",
    backgroundColor: theme.palette.background.paper,
  }),

  // Selections state: solid border, no background
  ...(hasSelections && {
    borderStyle: "solid",
    backgroundColor: "transparent",
  }),

  // Hover state or picker open (no selections): grey background
  ...(!hasSelections && {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  }),

  // Hover state (with selections): keep transparent
  ...(hasSelections && {
    "&:hover": {
      backgroundColor: "transparent",
      borderColor: theme.palette.primary.main,
    },
  }),

  // When picker is open (no selections): grey background
  ...(isOpen &&
    !hasSelections && {
      backgroundColor: theme.palette.action.hover,
    }),

  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },

  "&.Mui-disabled": {
    opacity: 0.6,
    cursor: "not-allowed",
    pointerEvents: "auto",
  },

  "& .MuiChip-icon": {
    fontSize: 18,
    marginLeft: theme.spacing(0.5),
  },

  "& .MuiChip-deleteIcon": {
    fontSize: 18,
    marginRight: theme.spacing(0.5),
    "&:hover": {
      color: theme.palette.error.main,
    },
  },
}));

export const SelectionLabel = styled(Box)(({ theme }) => ({
  display: "inline",
  "& .label-text": {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightRegular,
  },
  "& .items-text": {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
    marginLeft: theme.spacing(0.5),
  },
}));

export const StyledPopper = styled(Popper)(() => ({
  zIndex: 1300,
  width: 300,
}));

export const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.background.paper,
  "& .MuiAutocomplete-paper": {
    marginTop: 0,
  },
  "& .MuiAutocomplete-listbox": {
    maxHeight: 320,
  },
})) as typeof Autocomplete;

export const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

export const StyledSearchIcon = styled(SearchIcon)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginRight: theme.spacing(1),
}));
